import React, { useEffect, useRef, useState } from 'react';
import { supabase } from '../supabase';
import { showToast } from '../utils/toast';
import {
  CONTACT_EMAIL,
  PRO_CHAIN,
  getPlan,
  type PlanId,
} from '../config/plans';
import './UpgradeModal.css';

interface UpgradeModalProps {
  open: boolean;
  onClose: () => void;
  planId: PlanId;
}

interface Invoice {
  invoiceId: string;
  depositAddress: string;
  amountUsd: number;
  expiresAt: string;
  plan: string;
}

type Phase = 'creating' | 'paying' | 'checking' | 'activated' | 'expired' | 'failed';

/**
 * Unique-address auto-upgrade. Each Upgrade click mints its own one-time
 * deposit address (a forwarder owned by our factory contract) valid for 10
 * minutes. The Verify button / auto-poll checks that address on-chain; on
 * payment the plan flips automatically and funds sweep to the main wallet.
 * The tx-hash paste flow is kept as a manual fallback link at the bottom.
 */
const UpgradeModal: React.FC<UpgradeModalProps> = ({ open, onClose, planId }) => {
  const plan = getPlan(planId);
  const [phase, setPhase] = useState<Phase>('creating');
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(600);
  const [showManual, setShowManual] = useState(false);
  const [txHash, setTxHash] = useState('');
  const [senderWallet, setSenderWallet] = useState('');
  const [verifyingManual, setVerifyingManual] = useState(false);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(1);
  const [creatingStep, setCreatingStep] = useState(0);
  const pollRef = useRef<number | null>(null);

  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000';

  const authHeaders = async (): Promise<Record<string, string> | null> => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) return null;
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
    };
  };

  const createInvoice = async () => {
    setPhase('creating');
    setError('');
    setInvoice(null);
    setCreatingStep(0);
    // Abort the fetch at 25s so a hung server never spins forever.
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 25000);
    try {
      const headers = await authHeaders();
      if (!headers) {
        setError('Please log in again and retry.');
        setPhase('failed');
        return;
      }
      const res = await fetch(`${apiUrl}/api/pro/invoice`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ plan: plan.id }),
        signal: controller.signal,
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(
          body.error === 'PRO_FACTORY_NOT_CONFIGURED'
            ? 'Payments are not configured yet. Please try again later.'
            : body.error === 'INVALID_PLAN'
              ? 'This plan is not available right now.'
              : 'Could not create a payment address. Please retry.'
        );
        setPhase('failed');
        return;
      }
      setInvoice(body as Invoice);
      setSecondsLeft(Math.max(0, Math.floor((new Date(body.expiresAt).getTime() - Date.now()) / 1000)));
      setPhase('paying');
    } catch (err: any) {
      console.error('Pro invoice error:', err);
      setError(
        err?.name === 'AbortError'
          ? 'The server took too long to respond. Check your connection and retry.'
          : 'Could not reach the server. Check your connection and retry.'
      );
      setPhase('failed');
    } finally {
      window.clearTimeout(timeout);
    }
  };

  const checkInvoice = async (silent: boolean) => {
    if (!invoice) return;
    if (!silent) setPhase('checking');
    try {
      const headers = await authHeaders();
      if (!headers) {
        if (!silent) {
          showToast.error('Please log in again and retry');
          setPhase('paying');
        }
        return;
      }
      const res = await fetch(`${apiUrl}/api/pro/invoice/${invoice.invoiceId}`, { headers });
      const body = await res.json().catch(() => ({}));
      if (body.status === 'confirmed') {
        setPhase('activated');
        showToast.success('Payment confirmed — plan activated');
        if (pollRef.current) window.clearInterval(pollRef.current);
      } else if (body.status === 'expired') {
        setPhase('expired');
        if (pollRef.current) window.clearInterval(pollRef.current);
      } else if (!silent) {
        setPhase('paying');
        showToast.error('No payment detected yet — send the USDC first, then Verify.');
      } else {
        setPhase('paying');
      }
    } catch {
      if (!silent) {
        setPhase('paying');
        showToast.error('Could not check yet. Wait a moment and retry.');
      }
    }
  };
  // Fresh invoice every time the modal opens / plan changes.
  useEffect(() => {
    if (!open) return;
    setShowManual(false);
    setTxHash('');
    setSenderWallet('');
    setAttempt(1);
    setCreatingStep(0);
    createInvoice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, planId]);

  // Rotating status lines while the invoice is being created — the request
  // can take 10-20s on a cold Koyeb instance, so the loader talks to the user.
  useEffect(() => {
    if (!open || phase !== 'creating') return;
    const t = window.setInterval(() => {
      setCreatingStep((s) => (s + 1) % 3);
    }, 4000);
    return () => window.clearInterval(t);
  }, [open, phase]);

  // Countdown.
  useEffect(() => {
    if (!open || phase !== 'paying' || !invoice) return;
    const t = window.setInterval(() => {
      const left = Math.floor((new Date(invoice.expiresAt).getTime() - Date.now()) / 1000);
      setSecondsLeft(Math.max(0, left));
      if (left <= 0) {
        setPhase('expired');
        window.clearInterval(t);
        if (pollRef.current) window.clearInterval(pollRef.current);
      }
    }, 1000);
    return () => window.clearInterval(t);
  }, [open, phase, invoice]);

  // Auto-poll every 15s while paying (backend check is one balanceOf call).
  useEffect(() => {
    if (!open || phase !== 'paying' || !invoice) return;
    pollRef.current = window.setInterval(() => { void checkInvoice(true); }, 15000);
    return () => {
      if (pollRef.current) window.clearInterval(pollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, phase, invoice?.invoiceId]);

  useEffect(() => () => {
    if (pollRef.current) window.clearInterval(pollRef.current);
  }, []);

  if (!open) return null;

  const qrUrl = invoice
    ? `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(invoice.depositAddress)}`
    : '';
  const mm = Math.floor(secondsLeft / 60);
  const ss = String(secondsLeft % 60).padStart(2, '0');

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      showToast.success('Copied to clipboard');
    } catch {
      showToast.error('Could not copy, please select and copy manually');
    }
  };

  const verifyManual = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!txHash.trim() || !senderWallet.trim()) {
      showToast.error('Paste your transaction hash and sender wallet');
      return;
    }
    setVerifyingManual(true);
    try {
      const headers = await authHeaders();
      if (!headers) {
        showToast.error('Please log in again and retry');
        return;
      }
      const res = await fetch(`${apiUrl}/api/pro/verify-payment`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          plan: plan.id,
          txHash: txHash.trim(),
          senderWallet: senderWallet.trim(),
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.ok) {
        showToast.error('Verification failed. Double-check the hash and retry.');
        return;
      }
      setPhase('activated');
      showToast.success('Payment confirmed — plan activated');
    } catch (err) {
      console.error('Pro verify error:', err);
      showToast.error('Could not verify. Check your connection and retry.');
    } finally {
      setVerifyingManual(false);
    }
  };

  const close = () => {
    if (pollRef.current) window.clearInterval(pollRef.current);
    onClose();
  };
  return (
    <div className="upgrade-modal-overlay" onClick={close}>
      <div className="upgrade-modal" onClick={(e) => e.stopPropagation()}>
        <div className="upgrade-modal-header">
          <div>
            <h2>Upgrade to {plan.name}</h2>
            <p className="upgrade-modal-sub">
              {plan.priceUsd !== null
                ? `$${plan.priceUsd} ${plan.billingLabel}`
                : plan.billingLabel}
            </p>
          </div>
          <button className="upgrade-modal-close" onClick={close} aria-label="Close">
            &times;
          </button>
        </div>

        {phase === 'activated' ? (
          <div className="upgrade-modal-body">
            <div className="upgrade-success">
              <div className="upgrade-success-icon">&#10003;</div>
              <h3>Plan activated</h3>
              <p>
                Payment confirmed on Polygon. <strong>{plan.name}</strong>{' '}
                is now active — your sales pages have no revenue cap.
              </p>
              <button className="upgrade-primary-btn" onClick={close}>Done</button>
            </div>
          </div>
        ) : phase === 'creating' ? (
          <div className="upgrade-modal-body">
            <div className="upgrade-creating" aria-live="polite">
              <div className="upgrade-creating-top">
                <span className="upgrade-spinner" aria-hidden="true" />
                <div>
                  <div className="upgrade-creating-title">
                    {['Creating your address…', 'Talking to Polygon…', 'Almost there…'][creatingStep]}
                  </div>
                  <div className="upgrade-creating-sub">One-time address · expires in 10 min</div>
                </div>
              </div>
              <div className="upgrade-skeleton-block">
                <div className="upgrade-skeleton-line long" />
                <div className="upgrade-skeleton-qr" />
                <div className="upgrade-skeleton-line" />
                <div className="upgrade-skeleton-line short" />
              </div>
              <button
                type="button"
                className="upgrade-manual-toggle"
                onClick={() => { setAttempt((a) => a + 1); createInvoice(); }}
              >
                Taking too long? Tap to retry{attempt > 1 ? ` (try ${attempt})` : ''}
              </button>
            </div>
          </div>
        ) : phase === 'failed' ? (
          <div className="upgrade-modal-body">
            <div className="upgrade-success">
              <h3>Something went wrong</h3>
              <p>{error || 'Could not start the payment. Please retry.'}</p>
              <button className="upgrade-primary-btn" onClick={createInvoice}>Retry</button>
            </div>
          </div>
        ) : phase === 'expired' ? (
          <div className="upgrade-modal-body">
            <div className="upgrade-success">
              <h3>Address expired</h3>
              <p>This one-time address was valid for 10 minutes. Generate a new one.</p>
              <button className="upgrade-primary-btn" onClick={createInvoice}>
                Generate new address
              </button>
            </div>
          </div>
        ) : (
          <div className="upgrade-modal-body">
            <div className="upgrade-invoice-timer">
              Expires in <strong>{mm}:{ss}</strong>
            </div>
            <ol className="upgrade-steps">
              <li>Send <strong>${invoice?.amountUsd} USDC</strong> on <strong>Polygon</strong> to the address below.</li>
              <li>Tap <strong>Verify</strong> — done.</li>
            </ol>

            <div className="upgrade-pay-box">
              {invoice ? (
                <img className="upgrade-qr" src={qrUrl} alt="One-time payment QR" loading="lazy" />
              ) : (
                <div className="upgrade-qr-missing">Loading...</div>
              )}
              <div className="upgrade-pay-details">
                <span className="upgrade-label">Send {invoice?.amountUsd} USDC to</span>
                <code className="upgrade-address">{invoice?.depositAddress || '...'}</code>
                <div className="upgrade-pay-actions">
                  <button
                    type="button"
                    className="upgrade-copy-btn"
                    onClick={() => invoice && copy(invoice.depositAddress)}
                    disabled={!invoice}
                  >
                    Copy
                  </button>
                  {invoice && (
                    <a
                      className="upgrade-explorer-link"
                      href={`${PRO_CHAIN.explorerAddressUrl}${invoice.depositAddress}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Polygonscan
                    </a>
                  )}
                </div>
                <p className="upgrade-network-note">
                  USDC on Polygon only. Address expires in 10 min.
                </p>
              </div>
            </div>

            <button
              type="button"
              className="upgrade-primary-btn"
              onClick={() => void checkInvoice(false)}
              disabled={phase === 'checking'}
            >
              {phase === 'checking' ? 'Checking…' : 'Verify payment'}
            </button>
            <p className="upgrade-footnote">Auto-checks every 15s.</p>

            <button
              type="button"
              className="upgrade-manual-toggle"
              onClick={() => setShowManual((v) => !v)}
            >
              {showManual ? 'Hide manual tx-hash fallback' : 'Paid already? Verify with tx hash instead'}
            </button>
            {showManual && (
              <form className="upgrade-form" onSubmit={verifyManual}>
                <label className="upgrade-label" htmlFor="upgradeTxHash">
                  Transaction hash
                </label>
                <input
                  id="upgradeTxHash"
                  className="upgrade-input"
                  value={txHash}
                  onChange={(e) => setTxHash(e.target.value)}
                  placeholder="0x... (from your wallet or Polygonscan)"
                  spellCheck={false}
                />
                <label className="upgrade-label" htmlFor="upgradeSenderWallet">
                  Your sender wallet
                </label>
                <input
                  id="upgradeSenderWallet"
                  className="upgrade-input"
                  value={senderWallet}
                  onChange={(e) => setSenderWallet(e.target.value)}
                  placeholder="0x... wallet you paid from"
                  spellCheck={false}
                />
                <button
                  type="submit"
                  className="upgrade-primary-btn"
                  disabled={verifyingManual || !txHash.trim() || !senderWallet.trim()}
                >
                  {verifyingManual ? 'Verifying...' : 'Verify with tx hash'}
                </button>
              </form>
            )}
            <p className="upgrade-footnote">
              Trouble? Send your details to{' '}
              <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default UpgradeModal;
